import { useState, type FormEvent } from 'react'
import RequestError from '../components/RequestError'
import Logo from '../components/Logo'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { Button, Field } from '../components/ui'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../lib/useAuth'

const MIN_PASSWORD_LENGTH = 10

export default function Signup() {
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const [invitation] = useState(() => new URLSearchParams(window.location.hash.slice(1)))
  const inviteToken = invitation.get('invite') ?? undefined
  const [name, setName] = useState('')
  const [email, setEmail] = useState(invitation.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await signUp(email, password, name || undefined, inviteToken)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the account.')
      setBusy(false)
    }
  }

  // Whether this installation has GitHub sign-in at all. Optimistic while
  // loading so the button does not blink out on installs that have it.
  const { data: featureData, error: featureError, reload: retryFeatures } = useApi(() => api.features(), [])
  const github = featureData?.github ?? true

  // A private invitation opens signup directly. Other signed-out visitors
  // can request operator review; submitting a request never grants access.
  const canSignUp = Boolean(inviteToken) || featureData?.invite_only === false

  // Strictly `=== false` above, so an unresolved answer is not read as an
  // open installation. Until `features` replies this renders nothing rather
  // than redirecting: bouncing to sign-in and back would be worse on an open
  // installation than a blank moment is here.
  if (!canSignUp) {
    if (!featureData) return <div className="auth"><div className="auth-card">{featureError ? <RequestError message={featureError} retry={retryFeatures} /> : <p role="status">Preparing your account…</p>}</div></div>
    return <Navigate to="/request-invite" replace />
  }

  return (
    <div className="auth">
      <div className="auth-card">
        <Logo size={44} className="auth-logo" />

        <div className="stack auth-titles" style={{ gap: 4 }}>
          <h1>Create your account</h1>
          <p className="muted">
            Try free for 7 days — a Starter app and a Dev database, no card.
          </p>
        </div>

        {inviteToken && <p className="tiny muted">This invitation is for {invitation.get('email')}. Create your account to accept it.</p>}
        {error && <div className="alert error">{error}</div>}

        <form className="stack" onSubmit={submit}>
          <Field label="Name" htmlFor="name">
            <input
              id="name"
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label="Email" htmlFor="email">
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            hint={
              tooShort
                ? `${MIN_PASSWORD_LENGTH - password.length} more characters needed`
                : `At least ${MIN_PASSWORD_LENGTH} characters.`
            }
          >
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button variant="primary" type="submit" loading={busy} block>
            Create account
          </Button>
        </form>

        {/* Reached only where a sign-up can succeed, so GitHub is safe to
            offer here: its round trip ends at `approved_github_signup`, which
            refuses an email nobody invited. */}
        {github ? (
          <>
            <div className="divider">or</div>
            <a className="btn block" href={api.githubStartUrl()}>
              Continue with GitHub
            </a>
          </>
        ) : (
          <p className="tiny faint" style={{ textAlign: 'center' }}>
            GitHub sign-in is not configured on this installation, so email it
            is.
          </p>
        )}

        <p className="tiny muted" style={{ textAlign: 'center' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--brand-text)' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}

